import React, { useState, useEffect } from 'react';
import { useAppDispatch } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import {
    BuildingLibraryIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    XMarkIcon,
    UserIcon,
    CalendarIcon,
    CreditCardIcon
} from '@heroicons/react/24/outline';

interface BankDetails {
    accountNumber: string;
    bankName: string;
    ifscCode: string;
    branchName: string;
    accountHolderName: string;
    isVerified: boolean;
    verifiedBy?: {
        _id: string;
        firstName: string;
        lastName: string;
    };
    verifiedAt?: string;
    submittedAt: string;
}

interface UserWallet {
    _id: string;
    user: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        phone?: string;
    };
    bankDetails: BankDetails;
    currentBalance: number;
    verifiedBalance: number;
    pendingBalance: number;
    totalEarnings: number;
    withdrawalInfo: {
        totalApprovedWithdrawals: number;
        totalPendingWithdrawals: number;
        availableForWithdrawal: number;
        totalWithdrawn: number;
    };
}

const AdminBankVerification: React.FC = () => {
    const dispatch = useAppDispatch();
    const [wallets, setWallets] = useState<UserWallet[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedWallet, setSelectedWallet] = useState<UserWallet | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [verifying, setVerifying] = useState<string | null>(null);

    const fetchWallets = async () => {
        try {
            setLoading(true);
            const response = await api.get('/wallets/admin/bank-verification');
            if (response.data.success) {
                setWallets(response.data.data);
            }
        } catch (error: any) {
            console.error('Error fetching wallets:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch bank details',
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    };

    const verifyBankDetails = async (userId: string) => {
        try {
            setVerifying(userId);
            const response = await api.put(`/wallets/admin/verify-bank/${userId}`);
            if (response.data.success) {
                dispatch(showToast({
                    message: 'Bank details verified successfully',
                    type: 'success'
                }));
                await fetchWallets();
                setShowDetailsModal(false);
            }
        } catch (error: any) {
            console.error('Error verifying bank details:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to verify bank details',
                type: 'error'
            }));
        } finally {
            setVerifying(null);
        }
    };

    const openDetailsModal = (wallet: UserWallet) => {
        setSelectedWallet(wallet);
        setShowDetailsModal(true);
    };

    useEffect(() => {
        fetchWallets();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400 text-lg">Loading bank verification requests...</p>
                </div>
            </div>
        );
    }

    const pendingWallets = wallets.filter(wallet => !wallet.bankDetails.isVerified);
    const verifiedWallets = wallets.filter(wallet => wallet.bankDetails.isVerified);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-xl">
                            <BuildingLibraryIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Bank Verification</h1>
                            <p className="text-gray-600 dark:text-gray-400">Verify and approve user bank details</p>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Requests</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{wallets.length}</p>
                            </div>
                            <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                <BuildingLibraryIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending Verification</p>
                                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{pendingWallets.length}</p>
                            </div>
                            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Verified</p>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{verifiedWallets.length}</p>
                            </div>
                            <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                                <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pending Verification Section */}
                {pendingWallets.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
                        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pending Verification</h2>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Bank details awaiting admin verification</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                {pendingWallets.map((wallet) => (
                                    <div key={wallet._id} className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-yellow-900/10 rounded-xl border border-yellow-200 dark:border-yellow-800">
                                        <div className="flex items-center gap-4">
                                            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                                                <UserIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 dark:text-white">
                                                    {wallet.user.firstName} {wallet.user.lastName}
                                                </h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">{wallet.user.email}</p>
                                                <div className="flex items-center gap-4 mt-1">
                                                    <p className="text-xs text-gray-500 dark:text-gray-500">
                                                        Submitted: {new Date(wallet.bankDetails.submittedAt).toLocaleDateString()}
                                                    </p>
                                                    <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                                                        Balance: ₹{wallet.currentBalance?.toLocaleString() || '0'}
                                                    </p>
                                                    <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                                        Available: ₹{wallet.withdrawalInfo?.availableForWithdrawal?.toLocaleString() || '0'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => openDetailsModal(wallet)}
                                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                                            >
                                                <EyeIcon className="h-4 w-4" />
                                                View Details
                                            </button>
                                            <button
                                                onClick={() => verifyBankDetails(wallet.user._id)}
                                                disabled={verifying === wallet.user._id}
                                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
                                            >
                                                {verifying === wallet.user._id ? (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                ) : (
                                                    <CheckCircleIcon className="h-4 w-4" />
                                                )}
                                                {verifying === wallet.user._id ? 'Verifying...' : 'Verify'}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Verified Section */}
                {verifiedWallets.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                                    <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Verified Bank Details</h2>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Bank details that have been verified</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                {verifiedWallets.map((wallet) => (
                                    <div key={wallet._id} className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/10 rounded-xl border border-green-200 dark:border-green-800">
                                        <div className="flex items-center gap-4">
                                            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                                                <UserIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 dark:text-white">
                                                    {wallet.user.firstName} {wallet.user.lastName}
                                                </h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">{wallet.user.email}</p>
                                                <div className="flex items-center gap-4 mt-1">
                                                    <p className="text-xs text-gray-500 dark:text-gray-500">
                                                        Verified: {wallet.bankDetails.verifiedAt ? new Date(wallet.bankDetails.verifiedAt).toLocaleDateString() : 'N/A'}
                                                    </p>
                                                    <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                                                        Balance: ₹{wallet.currentBalance?.toLocaleString() || '0'}
                                                    </p>
                                                    <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                                        Available: ₹{wallet.withdrawalInfo?.availableForWithdrawal?.toLocaleString() || '0'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 rounded-full text-sm font-medium">
                                                Verified
                                            </span>
                                            <button
                                                onClick={() => openDetailsModal(wallet)}
                                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                                            >
                                                <EyeIcon className="h-4 w-4" />
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Bank Details Modal */}
                {showDetailsModal && selectedWallet && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Bank Details - {selectedWallet.user.firstName} {selectedWallet.user.lastName}
                                </h3>
                                <button
                                    onClick={() => setShowDetailsModal(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* User Information */}
                                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl">
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <UserIcon className="h-5 w-5" />
                                        User Information
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                                            <p className="text-sm text-gray-900 dark:text-white">{selectedWallet.user.firstName} {selectedWallet.user.lastName}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                                            <p className="text-sm text-gray-900 dark:text-white">{selectedWallet.user.email}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
                                            <p className="text-sm text-gray-900 dark:text-white">{selectedWallet.user.phone || 'Not provided'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Current Balance</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-semibold">₹{selectedWallet.currentBalance?.toLocaleString() || '0'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Verified Balance</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-semibold">₹{selectedWallet.verifiedBalance?.toLocaleString() || '0'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Pending Balance</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-semibold">₹{selectedWallet.pendingBalance?.toLocaleString() || '0'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Total Earnings</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-semibold">₹{selectedWallet.totalEarnings?.toLocaleString() || '0'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Withdrawal Information */}
                                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl">
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <CreditCardIcon className="h-5 w-5" />
                                        Withdrawal Information
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Available for Withdrawal</label>
                                            <p className="text-sm text-green-600 dark:text-green-400 font-semibold">₹{selectedWallet.withdrawalInfo?.availableForWithdrawal?.toLocaleString() || '0'}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Current Balance - Approved Withdrawals</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Total Approved Withdrawals</label>
                                            <p className="text-sm text-blue-600 dark:text-blue-400 font-semibold">₹{selectedWallet.withdrawalInfo?.totalApprovedWithdrawals?.toLocaleString() || '0'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Pending Withdrawals</label>
                                            <p className="text-sm text-yellow-600 dark:text-yellow-400 font-semibold">₹{selectedWallet.withdrawalInfo?.totalPendingWithdrawals?.toLocaleString() || '0'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Total Withdrawn</label>
                                            <p className="text-sm text-purple-600 dark:text-purple-400 font-semibold">₹{selectedWallet.withdrawalInfo?.totalWithdrawn?.toLocaleString() || '0'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Bank Details */}
                                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl">
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <BuildingLibraryIcon className="h-5 w-5" />
                                        Bank Details
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Account Number</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-mono">{selectedWallet.bankDetails.accountNumber}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Bank Name</label>
                                            <p className="text-sm text-gray-900 dark:text-white">{selectedWallet.bankDetails.bankName}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">IFSC Code</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-mono">{selectedWallet.bankDetails.ifscCode}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Branch Name</label>
                                            <p className="text-sm text-gray-900 dark:text-white">{selectedWallet.bankDetails.branchName}</p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Account Holder Name</label>
                                            <p className="text-sm text-gray-900 dark:text-white">{selectedWallet.bankDetails.accountHolderName}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Verification Status */}
                                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl">
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <CheckCircleIcon className="h-5 w-5" />
                                        Verification Status
                                    </h4>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${selectedWallet.bankDetails.isVerified
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                                                }`}>
                                                {selectedWallet.bankDetails.isVerified ? 'Verified' : 'Pending Verification'}
                                            </span>
                                            {selectedWallet.bankDetails.verifiedAt && (
                                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                                    Verified on {new Date(selectedWallet.bankDetails.verifiedAt).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                        {!selectedWallet.bankDetails.isVerified && (
                                            <button
                                                onClick={() => verifyBankDetails(selectedWallet.user._id)}
                                                disabled={verifying === selectedWallet.user._id}
                                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
                                            >
                                                {verifying === selectedWallet.user._id ? (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                ) : (
                                                    <CheckCircleIcon className="h-4 w-4" />
                                                )}
                                                {verifying === selectedWallet.user._id ? 'Verifying...' : 'Verify Bank Details'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminBankVerification;
